import { supabase } from '@/integrations/supabase/client';
import { Member } from '@/types/member';

export interface EnhancedMatchingSuggestion {
  id: string;
  member: Member;
  compatibility: number;
  reason: string;
  aiJustification?: string;
  categories: string[];
  score_breakdown: {
    industry_alignment: number;
    skill_complementarity: number;
    geographic_proximity: number;
    experience_level: number;
    language_overlap: number;
  };
}

export interface MatchingFilters {
  sector?: string;
  location?: string;
  seniorityLevel?: string;
  skills?: string[];
  maxDistance?: number;
}

class EnhancedMatchmakingService {
  // Cache persistant localStorage
  private readonly CACHE_KEY_PREFIX = 'enhanced_matchmaking_cache';
  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes - Réduit pour meilleure réactivité
  
  // Legacy in-memory cache
  private cache = new Map<string, EnhancedMatchingSuggestion[]>();
  private cacheExpiry = new Map<string, number>();

  /**
   * Récupère les suggestions depuis localStorage
   */
  private getCachedSuggestions(cacheKey: string): EnhancedMatchingSuggestion[] | null {
    try {
      const fullKey = `${this.CACHE_KEY_PREFIX}_${cacheKey}`;
      const cached = localStorage.getItem(fullKey);
      
      if (!cached) return null;
      
      const parsed = JSON.parse(cached);
      const now = Date.now();
      
      if (now - parsed.timestamp > this.CACHE_TTL) {
        console.log('🗑️ Cache expiré pour:', cacheKey);
        localStorage.removeItem(fullKey);
        return null;
      }
      
      console.log('✅ Cache localStorage hit pour:', cacheKey);
      return parsed.data;
    } catch (error) {
      console.error('❌ Erreur lecture cache:', error);
      return null;
    }
  }

  /**
   * Sauvegarde les suggestions dans localStorage
   */
  private setCachedSuggestions(cacheKey: string, suggestions: EnhancedMatchingSuggestion[]): void {
    try {
      const fullKey = `${this.CACHE_KEY_PREFIX}_${cacheKey}`;
      const cacheData = {
        data: suggestions,
        timestamp: Date.now()
      };
      
      localStorage.setItem(fullKey, JSON.stringify(cacheData));
      console.log('💾 Suggestions sauvegardées dans localStorage:', cacheKey);
    } catch (error) {
      console.error('❌ Erreur sauvegarde cache:', error);
      this.clearOldCaches();
    }
  }

  /**
   * Nettoie les anciens caches localStorage
   */
  private clearOldCaches(): void {
    try {
      const keysToRemove: string[] = [];
      const now = Date.now();
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_KEY_PREFIX)) {
          try {
            const cached = localStorage.getItem(key);
            if (!cached) {
              keysToRemove.push(key);
              continue;
            }
            
            const parsed = JSON.parse(cached);
            
            // Vérifier structure valide
            if (!parsed.data || !parsed.timestamp) {
              console.warn('🗑️ Cache corrompu détecté:', key);
              keysToRemove.push(key);
              continue;
            }
            
            // Vérifier expiration
            if (now - parsed.timestamp > this.CACHE_TTL) {
              console.log('⏰ Cache expiré:', key);
              keysToRemove.push(key);
            }
          } catch (parseError) {
            console.error('❌ Cache corrompu non parsable:', key);
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`🧹 Nettoyage effectué: ${keysToRemove.length} cache(s) supprimé(s)`);
      }
    } catch (error) {
      console.error('❌ Erreur nettoyage cache:', error);
    }
  }

  /**
   * Vide tous les caches de matchmaking (manuel)
   */
  clearPersistentCache(): void {
    this.clearOldCaches();
    this.cache.clear();
    this.cacheExpiry.clear();
    console.log('🗑️ Tous les caches matchmaking vidés');
  }

  /**
   * Vide le cache pour un utilisateur spécifique
   */
  clearCacheForUser(userId: string): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_KEY_PREFIX) && key.includes(userId)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`🗑️ Cache vidé pour l'utilisateur ${userId}: ${keysToRemove.length} entrée(s)`);
    } catch (error) {
      console.error('❌ Erreur vidage cache utilisateur:', error);
    }
  }

  /**
   * Récupère les IDs des utilisateurs déjà feedbackés (swipés)
   */
  private async getFeedbackedUserIds(userId: string): Promise<Set<string>> {
    try {
      const { data, error } = await supabase
        .from('networking_events')
        .select('target_user_id')
        .eq('user_id', userId)
        .eq('event_type', 'matchmaking_feedback')
        .not('target_user_id', 'is', null);

      if (error) {
        console.error('❌ Erreur récupération feedbacks:', error);
        return new Set();
      }

      const feedbackedIds = new Set<string>();
      data?.forEach(evt => {
        if (evt.target_user_id) feedbackedIds.add(evt.target_user_id);
      });

      console.log(`👁️ ${feedbackedIds.size} profil(s) déjà vu(s) pour ${userId}`);
      return feedbackedIds;
    } catch (error) {
      console.error('❌ Erreur getFeedbackedUserIds:', error);
      return new Set();
    }
  }

  /**
   * Récupère les IDs des utilisateurs déjà connectés ou en attente
   */
  private async getExistingConnectionUserIds(userId: string): Promise<Set<string>> {
    try {
      const { data, error } = await supabase
        .from('connection_requests')
        .select('requester_id, requested_id')
        .or(`requester_id.eq.${userId},requested_id.eq.${userId}`)
        .in('status', ['pending', 'accepted']);
      
      if (error) {
        console.error('❌ Erreur récupération connexions:', error);
        return new Set();
      }
      
      const connectedUserIds = new Set<string>();
      data?.forEach(conn => {
        if (conn.requester_id !== userId) connectedUserIds.add(conn.requester_id);
        if (conn.requested_id !== userId) connectedUserIds.add(conn.requested_id);
      });
      
      console.log(`🔗 ${connectedUserIds.size} connexion(s) existante(s) pour ${userId}`);
      return connectedUserIds;
    } catch (error) {
      console.error('❌ Erreur getExistingConnectionUserIds:', error);
      return new Set();
    }
  }

  async fetchEnhancedMatchmakingSuggestions(
    currentMember: Member, 
    filters?: MatchingFilters,
    limit: number = 10
  ): Promise<EnhancedMatchingSuggestion[]> {
    if (!currentMember.id || !currentMember.user_id) return [];

    const cacheKey = `${currentMember.user_id}_${JSON.stringify(filters)}_${limit}`;
    
    // Vérifier cache localStorage AVANT tout
    const cachedSuggestions = this.getCachedSuggestions(cacheKey);
    if (cachedSuggestions) {
      console.log('⚡ Chargement depuis cache - <100ms');
      return cachedSuggestions;
    }

    console.log('🔍 Pas de cache - Calcul des suggestions...');
    const startTime = Date.now();

    try {
      // Récupérer les connexions existantes ET les profils déjà swipés
      const [existingConnectionUserIds, feedbackedUserIds] = await Promise.all([
        this.getExistingConnectionUserIds(currentMember.user_id),
        this.getFeedbackedUserIds(currentMember.user_id),
      ]);

      // Fusionner les exclusions
      const allExcludedUserIds = new Set([...existingConnectionUserIds, ...feedbackedUserIds]);
      
      // Fetch depuis Supabase (uniquement si pas de cache)
      let query = supabase
        .from('club_members')
        .select('id, firstname, lastname, name, email, company, role, image, city, country, skills, industry, languages, bio, needs, user_id, created_at')
        .neq('id', currentMember.id)
        .eq('is_visible_in_directory', true)
        .eq('status', 'actif')
        .limit(50);

      // Filtrer les membres déjà connectés ET déjà swipés
      if (allExcludedUserIds.size > 0) {
        const excludedUserIds = Array.from(allExcludedUserIds);
        query = query.not('user_id', 'in', `(${excludedUserIds.join(',')})`);
      }

      const { data: allMembers, error } = await query;

      if (error) throw error;

      console.log('🔍 Filtrage membres:', {
        totalFetched: allMembers?.length || 0,
        excludedConnections: existingConnectionUserIds.size,
        remainingAfterFilter: allMembers?.length || 0
      });

      // Calculer les scores de compatibilité
      let suggestions = await this.calculateCompatibilityScores(currentMember, allMembers || []);

      // Appliquer les filtres
      if (filters) {
        suggestions = this.applyFilters(suggestions, filters);
      }

      // Filtrer par seuil minimum (30%)
      suggestions = suggestions.filter(s => s.compatibility >= 30);

      // Trier par compatibilité
      suggestions = suggestions.sort((a, b) => b.compatibility - a.compatibility);

      // Mode Discovery si < 3 suggestions
      if (suggestions.length < 3) {
        const { data: recentMembers } = await supabase
          .from('club_members')
          .select('id, firstname, lastname, name, email, company, role, image, city, country, skills, industry, languages, bio, needs, user_id, created_at')
          .neq('id', currentMember.id)
          .eq('is_visible_in_directory', true)
          .eq('status', 'actif')
          .order('created_at', { ascending: false })
          .limit(5);
        
        if (recentMembers) {
          const recentSuggestions = await this.calculateCompatibilityScores(currentMember, recentMembers);
          const existingIds = new Set(suggestions.map(s => s.id));
          const newRecentSuggestions = recentSuggestions.filter(s => !existingIds.has(s.id));
          suggestions.push(...newRecentSuggestions.slice(0, 3));
        }
      }

      // Limiter aux top résultats
      suggestions = suggestions.slice(0, limit);

      // Ajouter justifications IA (uniquement pour top 3)
      await this.enhanceWithAIJustifications(suggestions.slice(0, 3), currentMember);

      // SAUVEGARDER dans localStorage
      this.setCachedSuggestions(cacheKey, suggestions);

      const duration = Date.now() - startTime;
      console.log(`✅ Suggestions calculées en ${duration}ms et sauvegardées`);

      return suggestions;
    } catch (error) {
      console.error('❌ Erreur fetchEnhancedMatchmakingSuggestions:', error);
      return [];
    }
  }

  private async calculateCompatibilityScores(
    currentMember: Member,
    potentialMatches: Member[]
  ): Promise<EnhancedMatchingSuggestion[]> {
    return potentialMatches.map(member => {
      const scores = {
        industry_alignment: this.calculateIndustryAlignment(currentMember, member),
        skill_complementarity: this.calculateSkillComplementarity(currentMember, member),
        geographic_proximity: this.calculateGeographicProximity(currentMember, member),
        experience_level: this.calculateExperienceAlignment(currentMember, member),
        language_overlap: this.calculateLanguageOverlap(currentMember, member),
      };

      let compatibility = this.calculateOverallCompatibility(scores);
      
      // Apply fallback scoring for incomplete profiles
      if (compatibility < 30) {
        const fallbackScore = this.calculateFallbackScore(member, currentMember);
        compatibility = Math.max(compatibility, fallbackScore);
      }

      const categories = this.getMatchingCategories(currentMember, member);
      const reason = this.generateBasicReason(scores, categories, compatibility);

      return {
        id: member.id!,
        member,
        compatibility,
        reason,
        categories,
        score_breakdown: scores,
      };
    });
  }

  private calculateFallbackScore(member: Member, currentMember: Member): number {
    let score = 0;
    
    // Same country bonus
    if (member.country && currentMember.country && 
        member.country.toLowerCase() === currentMember.country.toLowerCase()) {
      score += 20;
    }
    
    // Same city bonus
    if (member.city && currentMember.city && 
        member.city.toLowerCase() === currentMember.city.toLowerCase()) {
      score += 30;
    }
    
    // Has real company
    if (member.company && member.company !== 'Non renseigné' && member.company.trim() !== '') {
      score += 15;
    }
    
    // Has bio
    if (member.bio && member.bio.length > 50) {
      score += 10;
    }
    
    // Has industry
    if (member.industry && member.industry.trim() !== '') {
      score += 15;
    }
    
    // Has skills
    if (member.skills && member.skills.length > 0) {
      score += 20;
    }
    
    return Math.min(score, 60); // Cap at 60% for fallback
  }

  private calculateIndustryAlignment(member1: Member, member2: Member): number {
    if (!member1.industry || !member2.industry) return 0;
    
    // Same industry = high score
    if (member1.industry.toLowerCase() === member2.industry.toLowerCase()) {
      return 0.9;
    }

    // Related industries (simple keyword matching)
    const industry1Keywords = member1.industry.toLowerCase().split(' ');
    const industry2Keywords = member2.industry.toLowerCase().split(' ');
    
    const overlap = industry1Keywords.filter(keyword => 
      industry2Keywords.some(k => k.includes(keyword) || keyword.includes(k))
    );

    return Math.min(0.8, overlap.length * 0.3);
  }

  private calculateSkillComplementarity(member1: Member, member2: Member): number {
    if (!member1.skills || !member2.skills || !member1.needs) return 0;

    const member1Skills = member1.skills.map(s => s.toLowerCase());
    const member1Needs = member1.needs.map(n => n.toLowerCase());
    const member2Skills = member2.skills.map(s => s.toLowerCase());

    // Check if member2's skills match member1's needs
    const skillMatches = member1Needs.filter(need =>
      member2Skills.some(skill => 
        skill.includes(need) || need.includes(skill)
      )
    );

    return Math.min(1.0, skillMatches.length * 0.4);
  }

  private calculateGeographicProximity(member1: Member, member2: Member): number {
    if (!member1.city || !member2.city) return 0.1;

    // Same city = high score
    if (member1.city.toLowerCase() === member2.city.toLowerCase()) {
      return 1.0;
    }

    // Same country = medium score
    if (member1.country && member2.country &&
        member1.country.toLowerCase() === member2.country.toLowerCase()) {
      return 0.6;
    }

    return 0.2;
  }

  private calculateExperienceAlignment(member1: Member, member2: Member): number {
    if (!member1.role || !member2.role) return 0.5;

    const role1 = member1.role.toLowerCase();
    const role2 = member2.role.toLowerCase();

    // Senior-level roles complement each other
    const seniorRoles = ['ceo', 'cto', 'founder', 'director', 'vp', 'head'];
    const isSenior1 = seniorRoles.some(role => role1.includes(role));
    const isSenior2 = seniorRoles.some(role => role2.includes(role));

    if (isSenior1 && isSenior2) return 0.8;
    if (isSenior1 || isSenior2) return 0.6;

    return 0.4;
  }

  private calculateLanguageOverlap(member1: Member, member2: Member): number {
    if (!member1.languages || !member2.languages) return 0.5;

    const lang1 = member1.languages.map(l => l.toLowerCase());
    const lang2 = member2.languages.map(l => l.toLowerCase());

    const overlap = lang1.filter(lang => lang2.includes(lang));
    return Math.min(1.0, overlap.length * 0.4);
  }

  private calculateOverallCompatibility(scores: any): number {
    const weights = {
      industry_alignment: 0.25,
      skill_complementarity: 0.30,
      geographic_proximity: 0.20,
      experience_level: 0.15,
      language_overlap: 0.10,
    };

    return Object.entries(scores).reduce((total, [key, score]) => {
      return total + (score as number) * weights[key as keyof typeof weights];
    }, 0) * 100; // Convert to percentage
  }

  private getMatchingCategories(member1: Member, member2: Member): string[] {
    const categories: string[] = [];

    if (member1.industry === member2.industry) {
      categories.push('Même secteur');
    }

    if (member1.city === member2.city) {
      categories.push('Même ville');
    }

    if (member1.skills && member2.skills && 
        member1.skills.some(s => member2.skills?.includes(s))) {
      categories.push('Compétences communes');
    }

    if (member1.needs && member2.skills &&
        member1.needs.some(need => member2.skills?.some(skill => 
          skill.toLowerCase().includes(need.toLowerCase())))) {
      categories.push('Besoins complémentaires');
    }

    return categories;
  }

  private generateBasicReason(scores: any, categories: string[], compatibility: number): string {
    if (categories.length > 0) {
      return `${categories.join(', ')} - Score global: ${Math.round(compatibility)}%`;
    }

    if (scores.skill_complementarity > 0.5) {
      return 'Compétences complémentaires détectées';
    }

    if (scores.geographic_proximity > 0.8) {
      return 'Proximité géographique idéale';
    }

    if (compatibility < 50) {
      return 'Nouveau membre à découvrir';
    }

    return 'Profil compatible pour le networking';
  }

  private async enhanceWithAIJustifications(
    suggestions: EnhancedMatchingSuggestion[],
    currentMember: Member
  ): Promise<void> {
    // Only enhance suggestions with AI to manage API costs
    for (const suggestion of suggestions) {
      try {
        const prompt = this.buildAIPrompt(currentMember, suggestion.member, suggestion.score_breakdown);
        
        const { data, error } = await supabase.functions.invoke('ai', {
          body: { message: prompt }
        });

        if (!error && data?.response) {
          suggestion.aiJustification = data.response;
        }
      } catch (error) {
        console.error('Error getting AI justification:', error);
        // Continue without AI justification
      }
    }
  }

  private buildAIPrompt(member1: Member, member2: Member, scores: any): string {
    return `Analyse cette connexion business potentielle et explique en 2-3 phrases pourquoi ces profils seraient complémentaires:

Profil 1: ${member1.firstname} ${member1.lastname}
- Entreprise: ${member1.company}
- Rôle: ${member1.role}
- Secteur: ${member1.industry}
- Compétences: ${member1.skills?.join(', ')}
- Besoins: ${member1.needs?.join(', ')}
- Localisation: ${member1.city}, ${member1.country}

Profil 2: ${member2.firstname} ${member2.lastname}
- Entreprise: ${member2.company}
- Rôle: ${member2.role}
- Secteur: ${member2.industry}
- Compétences: ${member2.skills?.join(', ')}
- Localisation: ${member2.city}, ${member2.country}

Scores de compatibilité:
- Alignement sectoriel: ${Math.round(scores.industry_alignment * 100)}%
- Complémentarité compétences: ${Math.round(scores.skill_complementarity * 100)}%
- Proximité géographique: ${Math.round(scores.geographic_proximity * 100)}%

Génère une justification business claire et concrète de cette connexion potentielle.`;
  }

  private applyFilters(
    suggestions: EnhancedMatchingSuggestion[],
    filters: MatchingFilters
  ): EnhancedMatchingSuggestion[] {
    return suggestions.filter(suggestion => {
      if (filters.sector && suggestion.member.industry !== filters.sector) {
        return false;
      }

      if (filters.location && suggestion.member.city !== filters.location) {
        return false;
      }

      if (filters.skills && filters.skills.length > 0) {
        const hasMatchingSkill = filters.skills.some(skill =>
          suggestion.member.skills?.some(memberSkill =>
            memberSkill.toLowerCase().includes(skill.toLowerCase())
          )
        );
        if (!hasMatchingSkill) return false;
      }

      return true;
    });
  }

  private isCacheValid(cacheKey: string): boolean {
    const expiry = this.cacheExpiry.get(cacheKey);
    return expiry ? Date.now() < expiry : false;
  }

  clearCache(): void {
    this.cache.clear();
    this.cacheExpiry.clear();
  }
}

export const enhancedMatchmakingService = new EnhancedMatchmakingService();

// Legacy compatibility functions
export const fetchEnhancedMatchmakingSuggestions = (
  currentMember: Member,
  filters?: MatchingFilters,
  limit?: number
) => enhancedMatchmakingService.fetchEnhancedMatchmakingSuggestions(currentMember, filters, limit);

export const calculateEnhancedCompatibility = (member1: Member, member2: Member) => {
  // This is a simplified version for quick calculations
  const service = new EnhancedMatchmakingService();
  return service['calculateOverallCompatibility']({
    industry_alignment: service['calculateIndustryAlignment'](member1, member2),
    skill_complementarity: service['calculateSkillComplementarity'](member1, member2),
    geographic_proximity: service['calculateGeographicProximity'](member1, member2),
    experience_level: service['calculateExperienceAlignment'](member1, member2),
    language_overlap: service['calculateLanguageOverlap'](member1, member2),
  });
};